import { h, render, Fragment } from 'preact'
import { useState, useEffect, useLayoutEffect } from 'preact/hooks'
// import './pikaday.css'
import DatePicker from './Pikaday'
import { useCustomCompareEffect } from 'react-use'
import Autocomplete from './Autocomplete'
import Select from './Select'
import { Loading } from './Loading'

const mq = window.matchMedia('(min-width: 576px)')
const numberOfMonths = mq.matches ? 2 : 1

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
]

function strToDate(str) {
  const [match, year, month, day, hours, mins, seconds] = str.match(
    /([0-9]{4})-([0-9]{2})-([0-9]{2}) ([0-9]{2}):([0-9]{2}):([0-9]{2})/
  )
  return new Date(
    parseInt(year, 10),
    parseInt(month, 10) - 1,
    parseInt(day, 10),
    parseInt(hours, 10),
    parseInt(mins, 10),
    parseInt(seconds, 10)
  )
}

const icons = {
  Plane: props => (
    <svg
      width="22"
      height="22"
      viewBox="0 0 16 16"
      fill="none"
      class="absolute z-30 ml-6 pointer-events-none"
      style="top:calc(50% - 11px);left:-1px"
      {...props}
    >
      <path d="M12.628 5.14a1.239 1.239 0 0 0 0-1.77 1.243 1.243 0 0 0-1.769 0L9.717 4.512a.208.208 0 0 1-.148.06c-.624 0-1.195-.003-1.713-.007-2.776-.018-4.044-.024-4.574.5a1.025 1.025 0 0 0-.28.77c0 .978.875 1.013 2.2 1.066.444.017.963.038 1.55.093a.208.208 0 0 1 .128.355l-1.8 1.798a.208.208 0 0 1-.147.061h-.881c-.58 0-1.05.471-1.051 1.051 0 .786.578.95 1.19 1.125l.22.063c.067.02.12.073.14.14.02.07.04.137.057.202.167.594.342 1.208 1.13 1.208.58 0 1.05-.47 1.05-1.051v-.882c0-.055.023-.108.062-.148l1.8-1.798a.208.208 0 0 1 .354.127c.056.59.076 1.111.094 1.557.053 1.32.088 2.194 1.065 2.194.317.027.63-.08.865-.296.476-.483.458-1.529.423-3.82-.011-.696-.024-1.508-.026-2.451 0-.056.022-.11.062-.148l1.141-1.142z" />
    </svg>
  ),
  Calendar: props => (
    <svg
      width="22"
      height="22"
      viewBox="0 0 16 16"
      class="absolute z-30 pin-l ml-6 pointer-events-none"
      style="top:calc(50% - 11px);"
      {...props}
    >
      <g fill="none" fillRule="evenodd">
        <rect width="11" height="10" x="2.5" y="3.5" />
        <path strokeLinecap="square" d="M4.5 2.5v2m7-2v2m1 2h-9" />
      </g>
    </svg>
  ),
  Marker: props => (
    <svg
      width="22"
      height="22"
      viewBox="0 0 16 16"
      class="absolute z-30 ml-6 pointer-events-none"
      style="top:calc(50% - 11px);left:-1px"
      {...props}
    >
      <path
        fill="none"
        d="M8 2.355a3.75 3.75 0 0 1 3.75 3.75c0 1.773-2.75 6.19-3.54 7.425a.25.25 0 0 1-.42 0C7 12.296 4.25 7.879 4.25 6.105A3.75 3.75 0 0 1 8 2.355zm0 5.25a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z"
      />
    </svg>
  ),
  User: props => (
    <svg
      width="22"
      height="22"
      viewBox="0 0 16 16"
      fill="none"
      class="absolute z-30 ml-6 pointer-events-none"
      style="top:calc(50% - 11px);left:-1px"
      {...props}
    >
      <g
        fill="none"
        fillRule="evenodd"
        strokeLinecap="round"
        strokeLinejoin="round"
        transform="translate(2 2)"
      >
        <circle cx="6" cy="4.875" r="2.625" />
        <path d="M9.457 10.438a4.873 4.873 0 0 0-6.914 0" />
        <circle cx="6" cy="6" r="5.625" />
      </g>
    </svg>
  )
}

const colors = {
  yellow: '#fbe20a',
  grey: '#999'
}

function useFetch(url) {
  const [data, setData] = useState()
  useEffect(() => {
    let current = true
    window
      .fetch(url)
      .then(res => res.json())
      .then(data => {
        if (current) {
          setData(data)
        }
      })
    return () => {
      current = false
    }
  }, [url])
  return data
}

function highlight(fullStr, str) {
  return fullStr
    .split(new RegExp(`(${str})`, 'i'))
    .map((x, i) =>
      i % 2 === 0 ? x : <mark className="text-current bg-yellow">{x}</mark>
    )
}

function DestinationPicker({ date, value, onChange = () => {}, ...props }) {
  const [destinations, setDestinations] = useState([])
  const [state, setState] = useState('loading')

  useEffect(() => {
    let current = true
    if (date) {
      setState('loading')
      window
        .fetch(
          `https://lba-flights.production.parallax.dev/outboundFlightsByDay?day=${date.getFullYear()}-${date.getMonth() +
            1}-${date.getDate()}`
        )
        .then(res => res.json())
        .then(data => {
          if (current) {
            setState('ready')
            setDestinations(
              data.reduce((acc, flight) => {
                let existing = acc.find(
                  destination => destination.iata === flight.destination_iata
                )
                let f = {
                  airline: flight.airline_name,
                  id: flight.flight_ident,
                  date: strToDate(flight.scheduled_departure)
                }
                if (existing) {
                  existing.flights.push(f)
                } else {
                  acc.push({
                    name: flight.destination_name,
                    iata: flight.destination_iata,
                    country: flight.destination_country,
                    datetime: flight.scheduled_departure,
                    flights: [f]
                  })
                }
                return acc
              }, [])
            )
          }
        })
    }
    return () => {
      current = false
    }
  }, [date])

  return state === 'loading' ? (
    <Loading />
  ) : (
    <Autocomplete
      name="destination"
      label="Where are you going?"
      items={destinations}
      itemToString={item => (item ? `${item.name} - ${item.country}` : '')}
      getItemKey={item => item.name}
      onChange={onChange}
      filterKeys={['name', 'country', 'iata']}
      renderItemLabel={(item, inputValue) => {
        return (
          <Fragment>
            <div class="flex-auto sm:w-auto sm:table-cell pt-4 pb-4 pl-6 sm:pl-8 md:pl-10 pr-8">
              {highlight(item.name, inputValue)} -{' '}
              {highlight(item.country, inputValue)}
            </div>
            <div class="flex-auto sm:w-auto sm:table-cell pt-4 pb-4 pr-6 sm:pr-8 md:pr-10 text-right">
              {highlight(item.iata, inputValue)}
            </div>
          </Fragment>
        )
      }}
      icon={<icons.Marker stroke={value ? colors.grey : colors.yellow} />}
      append={[{ label: 'My destination isn’t listed', action: 'manual' }]}
      {...props}
    />
  )
}

function OutboundFlightPicker({
  destination,
  value,
  onChange = () => {},
  ...props
}) {
  return (
    <Fragment>
      {value && (
        <Fragment>
          <input type="hidden" name="departingFlight" value={value.id} />
          <input type="hidden" name="departingAirline" value={value.airline} />
          <input
            type="hidden"
            name="departingTime"
            value={`${value.date
              .getHours()
              .toString()
              .padStart(2, '0')}:${value.date
              .getMinutes()
              .toString()
              .padStart(2, '0')}`}
          />
        </Fragment>
      )}
      <Select
        label="Which flight are you departing on?"
        items={destination.flights}
        renderItemLabel={item => `${item.id} ${item.airline}`}
        getItemKey={item => item.id}
        value={value}
        onChange={onChange}
        renderItemLabel={item => {
          return (
            <Fragment>
              <div class="flex-auto sm:w-auto sm:table-cell pt-4 pb-4 pl-6 sm:pl-8 md:pl-10 pr-8 font-bold">
                {item.date
                  .getHours()
                  .toString()
                  .padStart(2, '0')}
                :
                {item.date
                  .getMinutes()
                  .toString()
                  .padStart(2, '0')}
              </div>
              <div class="flex-auto sm:w-full sm:table-cell pt-4 sm:pb-4">
                {item.airline} {item.id}
              </div>
            </Fragment>
          )
        }}
        renderSelectedItemLabel={item => {
          return `${item.date
            .getHours()
            .toString()
            .padStart(2, '0')}:${item.date
            .getMinutes()
            .toString()
            .padStart(2, '0')} - ${item.airline} ${item.id}`
        }}
        icon={<icons.Plane stroke={value ? colors.grey : colors.yellow} />}
        {...props}
      />
    </Fragment>
  )
}

function useDateCompareEffect(fn, date) {
  useCustomCompareEffect(fn, [date], ([prevDate], [nextDate]) => {
    if (prevDate === nextDate) return true
    if (!prevDate || !nextDate) return false
    return prevDate.getTime() === nextDate.getTime()
  })
}

function ParkingWidget() {
  const [departureDate, setDepartureDate] = useState()
  const [destination, setDestination] = useState()
  const [outboundFlight, setOutboundFlight] = useState()
  const [returnDate, setReturnDate] = useState()
  const [inboundFlight, setInboundFlight] = useState()

  const [manual, setManual] = useState(true)
  useLayoutEffect(() => {
    document.getElementById(
      document
        .getElementById('preact-parking-widget')
        .getAttribute('data-fallback')
    ).hidden = !manual
    setDepartureDate()
    setDestination()
    setOutboundFlight()
    setReturnDate()
    setInboundFlight()
  }, [manual])

  useDateCompareEffect(() => {
    setDestination()
    setOutboundFlight()
    setReturnDate()
    setInboundFlight()
  }, departureDate)

  useEffect(() => {
    setOutboundFlight()
    setReturnDate()
    setInboundFlight()
  }, [destination])

  useEffect(() => {
    setReturnDate()
    setInboundFlight()
  }, [outboundFlight])

  useDateCompareEffect(() => {
    setInboundFlight()
  }, returnDate)

  return (
    <Fragment>
      {!manual && (
        <form
          className="parking-widget-v2 relative z-20"
          method="get"
          action="/book/parking/options"
        >
          <p class="mt-3 mb-4">We just need a few details about your trip:</p>
          <DatePicker
            name="departingDate"
            style={{ zIndex: 5 }}
            value={departureDate}
            onChange={setDepartureDate}
            minDate={new Date()}
            startRange={departureDate}
            endRange={returnDate}
            label={
              departureDate
                ? `Departure Date: ${departureDate.getDate()}${nth(
                    departureDate.getDate()
                  )} ${
                    MONTHS[departureDate.getMonth()]
                  } ${departureDate.getFullYear()}`
                : 'When is your departing flight?'
            }
            numberOfMonths={numberOfMonths}
            icon={
              <icons.Calendar
                stroke={departureDate ? colors.grey : colors.yellow}
              />
            }
          />
          {departureDate && (
            <DestinationPicker
              style={{ zIndex: 4 }}
              date={departureDate}
              value={destination}
              onChange={val => {
                if (val.action === 'manual') {
                  setManual(true)
                } else {
                  setDestination(val)
                }
              }}
            />
          )}
          {destination && (
            <OutboundFlightPicker
              style={{ zIndex: 3 }}
              destination={destination}
              value={outboundFlight}
              onChange={setOutboundFlight}
            />
          )}
          {outboundFlight && (
            <InboundFlightDatePicker
              style={{ zIndex: 2, marginTop: 8 }}
              origin={destination.iata}
              value={returnDate}
              onChange={setReturnDate}
              minDate={departureDate}
              defaultDate={departureDate}
              startRange={departureDate}
              endRange={returnDate}
              label={
                returnDate
                  ? `Return Date: ${returnDate.getDate()}${nth(
                      returnDate.getDate()
                    )} ${
                      MONTHS[returnDate.getMonth()]
                    } ${returnDate.getFullYear()}`
                  : 'When does your return flight land?'
              }
              numberOfMonths={numberOfMonths}
              onBail={() => setManual(true)}
            />
          )}
          {returnDate && (
            <InboundFlightPicker
              style={{ zIndex: 1 }}
              origin={destination.iata}
              date={returnDate}
              value={inboundFlight}
              onChange={setInboundFlight}
            />
          )}
          {inboundFlight && (
            <div class="mt-3">
              <div class="flex justify-end">
                <button
                  type="submit"
                  class="relative w-full sm:max-w-button-max appearance-none tracking-wide inline-flex items-center justify-center font-secondary rounded tracking-normal no-underline transition-bg disabled:cursor-not-allowed bg-yellow hover:bg-yellow-dark active:bg-yellow-light disabled:bg-yellow-lighter disabled:text-yellow-dark text-black h-60 font-medium text-sm px-6"
                  style={{ minWidth: 140 }}
                >
                  Book Parking
                </button>
              </div>
            </div>
          )}
        </form>
      )}
    </Fragment>
  )
}

function InboundFlightPicker({
  origin,
  date,
  onChange = () => {},
  value,
  ...props
}) {
  const [state, setState] = useState('loading')
  const [flights, setFlights] = useState([])

  useEffect(() => {
    let current = true
    setState('loading')
    window
      .fetch(
        `https://lba-flights.production.parallax.dev/inboundFlightsByDay?day=${date.getFullYear()}-${date.getMonth() +
          1}-${date.getDate()}`
      )
      .then(res => res.json())
      .then(data => {
        if (current) {
          setState('ready')
          setFlights(data.filter(flight => flight.origin_iata === origin))
        }
      })

    return () => {
      current = false
    }
  }, [origin, date])

  console.log(value)

  return state === 'loading' ? (
    <Loading />
  ) : (
    <Fragment>
      {value && (
        <Fragment>
          <input
            type="hidden"
            name="returningFlight"
            value={value.flight_ident}
          />
          <input
            type="hidden"
            name="returningAirline"
            value={value.airline_name}
          />
          <input
            type="hidden"
            name="returningTime"
            value={value.scheduled_arrival.substr(11, 5)}
          />
        </Fragment>
      )}
      <Select
        label="Which flight are you returning on?"
        items={flights}
        renderItemLabel={item => `${item.flight_ident} ${item.airline_name}`}
        getItemKey={item => item.flight_ident}
        onChange={onChange}
        renderItemLabel={item => {
          const date = strToDate(item.scheduled_arrival)
          return (
            <Fragment>
              <div class="flex-auto sm:w-auto sm:table-cell pt-4 pb-4 pl-6 sm:pl-8 md:pl-10 pr-8 font-bold">
                {date
                  .getHours()
                  .toString()
                  .padStart(2, '0')}
                :
                {date
                  .getMinutes()
                  .toString()
                  .padStart(2, '0')}
              </div>
              <div class="flex-auto sm:w-full sm:table-cell pt-4 sm:pb-4">
                {item.airline_name} {item.flight_ident}
              </div>
            </Fragment>
          )
        }}
        renderSelectedItemLabel={item => {
          const date = strToDate(item.scheduled_arrival)
          return `${date
            .getHours()
            .toString()
            .padStart(2, '0')}:${date
            .getMinutes()
            .toString()
            .padStart(2, '0')} - ${item.airline_name} ${item.flight_ident}`
        }}
        icon={<icons.Plane stroke={value ? colors.grey : colors.yellow} />}
        {...props}
      />
    </Fragment>
  )
}

function InboundFlightDatePicker({
  origin,
  value,
  onChange = () => {},
  onBail,
  ...props
}) {
  const [validDates, setValidDates] = useState()

  useEffect(() => {
    let current = true
    window
      .fetch(
        `https://lba-flights.production.parallax.dev/inboundFlightsByOrigin?origin_iata=${origin}`
      )
      .then(res => res.json())
      .then(data => {
        if (current) {
          setValidDates(data)
        }
      })

    return () => {
      current = false
    }
  }, [origin, setValidDates])

  return (
    <DatePicker
      name="returningDate"
      value={value}
      onChange={onChange}
      disableDayFn={day => {
        if (!validDates) return true
        return (
          validDates.findIndex(
            d =>
              d.scheduled_arrival.substr(0, 10) ===
              `${day.getFullYear()}-${(day.getMonth() + 1)
                .toString()
                .padStart(2, '0')}-${day
                .getDate()
                .toString()
                .padStart(2, '0')}`
          ) === -1
        )
      }}
      icon={<icons.Calendar stroke={value ? colors.grey : colors.yellow} />}
      onBail={onBail}
      theme="pika-has-bail"
      {...props}
    />
  )
}

function nth(d) {
  if (d > 3 && d < 21) return 'th'
  switch (d % 10) {
    case 1:
      return 'st'
    case 2:
      return 'nd'
    case 3:
      return 'rd'
    default:
      return 'th'
  }
}

function LoungeWidget() {
  const [departureDate, setDepartureDate] = useState()
  const [destination, setDestination] = useState()
  const [outboundFlight, setOutboundFlight] = useState()
  const [adults, setAdults] = useState(2)
  const [children, setChildren] = useState(0)

  const [manual, setManual] = useState(true)
  useLayoutEffect(() => {
    document.getElementById(
      document
        .getElementById('preact-lounge-widget')
        .getAttribute('data-fallback')
    ).hidden = !manual
    setDepartureDate()
    setDestination()
    setOutboundFlight()
  }, [manual])

  useDateCompareEffect(() => {
    setDestination()
    setOutboundFlight()
  }, departureDate)

  useEffect(() => {
    setOutboundFlight()
  }, [destination])

  return (
    <Fragment>
      {!manual && (
        <form
          className="parking-widget-v2 relative z-20"
          method="get"
          action="/book/lounges/options"
        >
          <p class="mt-3 mb-4">We just need a few details about your trip:</p>
          <DatePicker
            name="departingDate"
            style={{ zIndex: 5 }}
            value={departureDate}
            onChange={setDepartureDate}
            minDate={new Date()}
            label={
              departureDate
                ? `Departure Date: ${departureDate.getDate()}${nth(
                    departureDate.getDate()
                  )} ${
                    MONTHS[departureDate.getMonth()]
                  } ${departureDate.getFullYear()}`
                : 'When is your departing flight?'
            }
            numberOfMonths={numberOfMonths}
            icon={
              <icons.Calendar
                stroke={departureDate ? colors.grey : colors.yellow}
              />
            }
          />
          {departureDate && (
            <DestinationPicker
              style={{ zIndex: 4 }}
              date={departureDate}
              value={destination}
              onChange={val => {
                if (val.action === 'manual') {
                  setManual(true)
                } else {
                  setDestination(val)
                }
              }}
            />
          )}
          {destination && (
            <OutboundFlightPicker
              style={{ zIndex: 3 }}
              destination={destination}
              value={outboundFlight}
              onChange={setOutboundFlight}
            />
          )}
          {outboundFlight && (
            <div class="relative flex flex-wrap -mx-2" style={{ zIndex: 2 }}>
              <div
                class="relative flex-none w-full sm:w-1/2 px-2"
                style={{ zIndex: 31 }}
              >
                <input type="hidden" name="adults" value={adults} />
                <Select
                  label="Adults (16+)"
                  items={[1, 2, 3, 4, 5, 6]}
                  renderItemLabel={item => item}
                  getItemKey={item => item}
                  value={adults}
                  onChange={setAdults}
                  renderItemLabel={item => {
                    return (
                      <div class="w-full px-6 sm:px-8 md:px-10 py-4">
                        {item}
                      </div>
                    )
                  }}
                  renderSelectedItemLabel={item => `Adults (16+): ${item}`}
                  icon={<icons.User stroke={colors.grey} />}
                />
              </div>
              <div class="relative flex-none w-full sm:w-1/2 px-2">
                <input type="hidden" name="children" value={children} />
                <Select
                  label="Children (2-15)"
                  items={[0, 1, 2, 3, 4]}
                  renderItemLabel={item => item}
                  getItemKey={item => item}
                  value={children}
                  onChange={setChildren}
                  renderItemLabel={item => {
                    return (
                      <div class="w-full px-6 sm:px-8 md:px-10 py-4">
                        {item}
                      </div>
                    )
                  }}
                  renderSelectedItemLabel={item => `Children (2-15): ${item}`}
                  icon={<icons.User stroke={colors.grey} />}
                />
              </div>
            </div>
          )}
          {outboundFlight && (
            <div class="mt-3">
              <div class="flex justify-end">
                <button
                  type="submit"
                  class="relative w-full sm:max-w-button-max appearance-none tracking-wide inline-flex items-center justify-center font-secondary rounded tracking-normal no-underline transition-bg disabled:cursor-not-allowed bg-yellow hover:bg-yellow-dark active:bg-yellow-light disabled:bg-yellow-lighter disabled:text-yellow-dark text-black h-60 font-medium text-sm px-6"
                  style={{ minWidth: 140 }}
                >
                  Find Lounges
                </button>
              </div>
            </div>
          )}
        </form>
      )}
    </Fragment>
  )
}

window.LBA.initPreactParkingWidget = () => {
  render(<ParkingWidget />, document.getElementById('preact-parking-widget'))
}

window.LBA.initPreactLoungeWidget = () => {
  render(<LoungeWidget />, document.getElementById('preact-lounge-widget'))
}



// WEBPACK FOOTER //
// ./src/js/widget/index.js